import { kybErrorMessage } from "../hooks/useKybLinks";
import { kybStyles } from "../kybStyles";
import React, { useId, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface KybInlineEditorProps {
  initialValue: string;
  label: string;
  inputMode?: "text" | "numeric";
  onSave: (value: string) => Promise<void>;
  onCancel: () => void;
}

const KybInlineEditor: React.FC<KybInlineEditorProps> = ({
  initialValue,
  label,
  inputMode = "text",
  onSave,
  onCancel,
}) => {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const errorId = useId();
  const { handleError } = useSnackbarMessage();

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) return;
    if (!value.trim()) {
      setError(`${label} is required.`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave(value.trim());
    } catch (err) {
      const message = kybErrorMessage(
        err,
        `Could not save ${label.toLowerCase()}. Try again.`,
      );
      setError(message);
      handleError({ message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="flex h-full w-full min-w-0 flex-col justify-center"
      onSubmit={save}
      aria-busy={saving}
    >
      <div className="flex min-w-0 items-center gap-1">
        <input
          autoFocus
          aria-label={label}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={kybStyles.input}
          value={value}
          inputMode={inputMode}
          placeholder={label}
          disabled={saving}
          onChange={(event) => {
            setValue(event.target.value);
            setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              if (!saving) onCancel();
            }
          }}
        />
        <button
          type="submit"
          className={kybStyles.saveIconButton}
          disabled={saving}
          title={saving ? "Saving…" : `Save ${label.toLowerCase()}`}
          aria-label={`Save ${label.toLowerCase()}`}
        >
          <span
            className={`material-symbols-outlined ${saving ? "animate-spin" : ""}`}
            aria-hidden="true"
          >
            {saving ? "progress_activity" : "check"}
          </span>
        </button>
        <button
          type="button"
          className={kybStyles.iconButton}
          disabled={saving}
          onClick={onCancel}
          title="Cancel edit"
          aria-label={`Cancel ${label.toLowerCase()} edit`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            close
          </span>
        </button>
      </div>
      {error && (
        <span
          id={errorId}
          className="block truncate mt-[2px] text-text-status-red text-[9px] leading-[12px]"
          role="alert"
          title={error}
        >
          {error}
        </span>
      )}
    </form>
  );
};

export default KybInlineEditor;
